import { useEffect, useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import SubSettingsShell from "@/components/SubSettingsShell";

/**
 * MyReports — iOS `settings/reports.tsx`.
 * Displays every report the current user has submitted, with the safety
 * team's status (pending / reviewing / resolved / dismissed).
 */

const STATUS_STYLES = {
  pending: { bg: "rgba(245, 158, 11, 0.14)", fg: "#F59E0B" },
  reviewing: { bg: "rgba(56, 132, 255, 0.14)", fg: "#3D9CFF" },
  resolved: { bg: "rgba(52, 211, 153, 0.14)", fg: "#34D399" },
  dismissed: { bg: "hsl(var(--secondary))", fg: "hsl(var(--muted-foreground))" },
};

export default function MyReports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // iOS spec: the app fetches user's submitted reports from
        // /api:brontoy-users-prod/me/reports (not /reports/mine).
        const res = await api.get(EP.meReports);
        const d = unwrap(res.data);
        const list = Array.isArray(d) ? d : d?.items || d?.reports || d?.data || [];
        setItems(list);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <SubSettingsShell
      title="My reports"
      subtitle="Reports you have submitted. Only you and our safety team can see this."
      testId="reports-screen"
    >
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#7638FA" }} />
        </div>
      ) : err ? (
        <div
          className="rounded-2xl p-4"
          style={{ backgroundColor: "rgba(255, 59, 48, 0.13)" }}
          data-testid="reports-error"
        >
          <p className="text-[#FF3B30] text-sm">{err}</p>
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-[20px] border border-border bg-card py-12 flex flex-col items-center text-center"
          data-testid="reports-empty"
        >
          <Flag size={22} className="text-muted-foreground mb-2.5" strokeWidth={1.7} />
          <p className="text-muted-foreground text-sm">
            You haven&apos;t submitted any reports.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {items.map((r) => {
            const id = r.id ?? r.report_id;
            const status = (r.status || "pending").toLowerCase();
            const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
            return (
              <div
                key={id}
                className="rounded-[20px] border border-border bg-card p-4"
                data-testid={`report-item-${id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-muted-foreground uppercase font-bold mb-1"
                      style={{ fontSize: 10.5, letterSpacing: "1.2px" }}
                    >
                      {r.target_type || "post"} · {r.reason?.replace(/_/g, " ") || "other"}
                    </div>
                    {r.details && (
                      <p className="text-foreground text-[14px] leading-5 mb-1.5 line-clamp-2">
                        {r.details}
                      </p>
                    )}
                    {r.created_at && (
                      <p className="text-muted-foreground text-[12px]">
                        {new Date(r.created_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <span
                    className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase"
                    style={{
                      backgroundColor: s.bg,
                      color: s.fg,
                      letterSpacing: "0.6px",
                    }}
                    data-testid={`report-status-${id}`}
                  >
                    {status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SubSettingsShell>
  );
}
